import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { MessageCircle, Send } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Comments = ({ newsId }) => {
  const { user, getAuthHeader } = useAuth();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [showComments, setShowComments] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (showComments) {
      fetchComments();
    }
  }, [newsId, showComments]);

  const fetchComments = async () => {
    try {
      const response = await axios.get(`${API}/comments/${newsId}`);
      setComments(response.data.comments || []);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Login to comment!');
      return;
    }
    if (!newComment.trim()) return;

    setLoading(true);
    try {
      await axios.post(
        `${API}/comments/${newsId}`,
        { text: newComment },
        { headers: getAuthHeader() }
      );
      setNewComment('');
      fetchComments();
      toast.success('Comment added!');
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-4" data-testid={`comments-section-${newsId}`}>
      <button
        onClick={() => setShowComments(!showComments)}
        data-testid="toggle-comments"
        className="flex items-center gap-2 text-sm font-bold text-gray-600 dark:text-gray-400 hover:text-black dark:hover:text-white transition-all"
      >
        <MessageCircle className="w-4 h-4" strokeWidth={2.5} />
        {comments.length > 0 ? `${comments.length} Comments` : 'Add Comment'}
      </button>

      {showComments && (
        <div className="mt-4 space-y-4">
          {user && (
            <form onSubmit={handleSubmitComment} className="flex gap-2">
              <input
                type="text"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment..."
                data-testid="comment-input"
                className="flex-1 border-2 border-black dark:border-white rounded-lg p-2 text-sm focus:ring-2 focus:ring-[#FF6B6B] focus:outline-none bg-white dark:bg-[#2D2A26] dark:text-white"
              />
              <button
                type="submit"
                disabled={loading || !newComment.trim()}
                data-testid="submit-comment"
                className="p-2 bg-[#FF6B6B] text-white rounded-lg border-2 border-black dark:border-white hover:bg-[#FF5252] transition-all disabled:opacity-50"
              >
                <Send className="w-4 h-4" strokeWidth={2.5} />
              </button>
            </form>
          )}

          <div className="space-y-3">
            {comments.map((comment) => (
              <div
                key={comment.id}
                className="bg-[#F0F0F0] dark:bg-[#3D3A36] border-2 border-black dark:border-gray-600 rounded-lg p-3"
                data-testid={`comment-${comment.id}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-bold text-gray-600 dark:text-gray-400">
                    {comment.user_email}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-500">
                    {format(new Date(comment.created_at), 'MMM dd, HH:mm')}
                  </span>
                </div>
                <p className="text-sm dark:text-white">{comment.text}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Comments;